import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { DepartmentModel } from 'src/app/models/management/department.model';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { DepartmentService } from '../../../../services/management/department.service';
import { CountryService } from './../../../../services/management/country.service';

@Component({
  selector: 'app-register-department',
  templateUrl: './register-department.component.html',
  styleUrls: ['./register-department.component.scss'],
  standalone: false
})
export class RegisterDepartmentComponent implements OnInit, OnDestroy {
  countryId: number = 0;
  departmentContract: DepartmentModel = new DepartmentModel();
  departmentId: number = 0;
  formDepartment: FormGroup = new FormGroup({});
  listCountries: any[] = [];
  subscription: Subscription;

  constructor(public config: DynamicDialogConfig,
    private countryService: CountryService,
    private departmentService: DepartmentService,
    private formBuilder: FormBuilder,
    @Optional() private ref: DynamicDialogRef,
    private toastHandlerService: ToastHandlerService) {
    this.getAllLists();
  }

  ngOnInit(): void {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.departmentContract = inputs.departmentModel;
      if (this.departmentContract.departmentId != 0) {
        this.departmentId = this.departmentContract.departmentId;
      }
      this.validateForm(new DepartmentModel());
      this.handleInitialState();
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async getAllLists() {
    try {
      const [countries] = await Promise.all([
        firstValueFrom(this.countryService.getAllCountriesForSelect()),
      ]);
      this.listCountries = countries;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  changeDepartment() {
    this.countryId = this.formDepartment.get("countryId")?.value;
    const code = this.formDepartment.get("code")?.value;

    if (!this.countryId || this.countryId == 0) {
      this.toastHandlerService.showToast('warning', 'Seleccione el páis.');
    }

    if (code != "") {
      this.getDepartmentByCode(this.countryId, code);
    } else {
      this.createNewDepartment();
    }
  }

  async createNewDepartment() {
    this.departmentId = 0;
    this.departmentContract = new DepartmentModel();
    if (this.countryId != 0) {
      this.departmentContract.countryId = this.countryId;
    }
    await this.validateForm(this.departmentContract);
  }

  async getCountry(countryId: number) {
    this.countryId = countryId;
    this.createNewDepartment();
  }

  async getDepartmentByCode(countryId: number, code: string) {
    try {
      this.departmentContract = await firstValueFrom(this.departmentService.getDepartmentByKeys(countryId, code));
      this.departmentId = this.departmentContract.departmentId;
      this.validateForm(this.departmentContract);
    } catch (error) {
      console.error('Error al buscar el departamento por código.', error);
    }
  }

  async getDepartmentById() {
    try {
      this.departmentContract = await firstValueFrom(this.departmentService.getDepartmentById(this.departmentId));
      this.departmentId = this.departmentContract.departmentId;
      await this.validateForm(this.departmentContract);
    } catch (error) {
      console.error('Error al buscar el departamento por id.', error);
    }
  }

  async handleInitialState() {
    if (this.departmentId !== null && this.departmentId !== 0) {
      await this.getDepartmentById();
    } else {
      this.createNewDepartment();
    }
  }

  async send() {
    Object.keys(this.formDepartment.controls).forEach(key => {
      const control = this.formDepartment.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });

    if (this.formDepartment.valid) {
      const modelDepartment = this.formDepartment.getRawValue();

      if (this.departmentId == 0) {
        try {
          const response = await firstValueFrom(this.departmentService.createDepartment(modelDepartment));
          this.toastHandlerService.showToast('success', `El departamento fue creado exitosamente.`);
          this.departmentContract = response;
          this.departmentId = this.departmentContract.departmentId;
          await this.validateForm(this.departmentContract);
        } catch (error) {
          console.error('Error al crear el departamento.', error);
        }
      } else {
        this.departmentContract.name = modelDepartment.name;
        try {
          const response = await firstValueFrom(this.departmentService.updateDepartment(this.departmentContract));
          this.toastHandlerService.showToast('success', `El departamento fue modificado exitosamente.`);
          this.departmentContract = response;
          await this.validateForm(this.departmentContract);
        } catch (error) {
          console.error('Error al actualizar el departamento.', error);
        }
      }
      const output$ = this.config.data?.output$;
      output$?.next({ type: 'reload' });
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      countryId: {
        required: 'El país es obligatirio.'
      },
      code: {
        required: 'La código es obligatirio.',
        maxlength: 'El campo código permite máximo 2 caracteres.'
      },
      name: {
        required: 'El nombre es obligatorio.',
        maxlength: 'El campo nombre permite máximo 30 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formDepartment.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm(department: DepartmentModel) {
    this.formDepartment = this.formBuilder.group({
      countryId: [department.countryId, [Validators.required]],
      code: [department.code, [Validators.required, Validators.maxLength(2)]],
      name: [department.name, [Validators.required, Validators.maxLength(30)]]
    });
  }
}
